import React, { useState, useEffect } from 'react';
import './../styles/LoginPage.css';
import AuthService from '../services/AuthService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isAndroid } from './../utils/deviceDetection';
import teamviewer from './../assets/images/teamviewer-50.png';
import companyLogo from './../assets/images/logo.png';
import DOMPurify from 'dompurify';
import validateInput from './../utils/validateInput';

const TEAMVIEWER_WINDOWS = process.env.REACT_APP_TEAMVIEWER_WINDOWS;
const TEAMVIEWER_MAC = process.env.REACT_APP_TEAMVIEWER_MAC;
const TRUSTED_DOMAINS = process.env.REACT_APP_TRUSTED_DOMAINS?.split(',') || [];

const isValidUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    return TRUSTED_DOMAINS.some((domain) => parsedUrl.hostname === domain.trim());
  } catch {
    return false;
  }
};

function LoginPage() {
  const [inputUsername, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const {
    login,
    token,
    userId,
    setUserId,
    setConstabulary,
    setMsiFile,
    setMsiUrl,
    setMacPack,
    setAndroidApk,
    setGroupId,
    setAndroid,
    setChromeUrl,
    setForcePrefix,
  } = useAuth();

  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (token && userId) {
      const isAndroidDevice = isAndroid();
      if (isAndroidDevice) {
        navigate('/android');
      } else {
        navigate('/notification');
      }
    }
  }, [token, userId, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const sanitizedUsername = DOMPurify.sanitize(inputUsername);
    const sanitizedPassword = DOMPurify.sanitize(password);
    const validationError = validateInput(sanitizedUsername, sanitizedPassword);

    if (validationError) {
      setError(validationError);
      setIsLoading(false);
      return;
    }

    try {
      const data = await AuthService.loginUser(sanitizedUsername, sanitizedPassword);
      setIsLoading(false);

      if (data?.user) {
        const {
          username,
          constabulary,
          msiFile,
          msiUrl,
          macPack,
          androidApk,
          groupId,
          android,
          chromeUrl,
          forcePrefix,
        } = data.user;

        login(username);

        setUserId(username);
        setConstabulary(constabulary);
        setMsiFile(msiFile);
        setMsiUrl(msiUrl);
        setMacPack(macPack);
        setAndroidApk(androidApk);
        setGroupId(groupId);
        setAndroid(android);
        setChromeUrl(chromeUrl);
        setForcePrefix(forcePrefix);

        const isAndroidDevice = isAndroid();
        if (isAndroidDevice) {
          navigate('/android');
        } else {
          navigate('/notification');
        }
      } else if (data?.error) {
        setError('Login failed. Check your credentials.');
        setUsername('');
        setPassword('');
      } else {
        setError('Login failed. Check your credentials.');
        setUsername('');
        setPassword('');
      }
    } catch (err) {
      setIsLoading(false);
      setError('Login failed. Check your credentials.');
      setUsername('');
      setPassword('');
      console.error(err);
    }
  };

  return (
    <div className="login-container">
      <div className="header-container">
        <div className="logo-container">
          <img src={companyLogo} alt="Company Logo" className="company-logo" />
        </div>
        <div className="opening-hours">
          <p>Open Monday to Friday (excl bank holidays) 09:00 - 17:00</p>
        </div>
      </div>

      <div className="teamviewer-links">
        <h3 className="teamviewer-title">Download TeamViewer:</h3>
        {isValidUrl(TEAMVIEWER_WINDOWS) && (
          <a
            href={TEAMVIEWER_WINDOWS}
            target="_blank"
            rel="noopener noreferrer"
            className="teamviewer-link"
          >
            <img src={teamviewer} alt="TeamViewer Windows" /> Windows
          </a>
        )}
        {isValidUrl(TEAMVIEWER_MAC) && (
          <a
            href={TEAMVIEWER_MAC}
            target="_blank"
            rel="noopener noreferrer"
            className="teamviewer-link"
          >
            <img src={teamviewer} alt="TeamViewer Mac" /> Mac
          </a>
        )}
        {(!isValidUrl(TEAMVIEWER_WINDOWS) || !isValidUrl(TEAMVIEWER_MAC)) && (
          <p className="error">
            TeamViewer links are currently unavailable. Please contact support.
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="login-form" aria-label="Login Form">
        <h2 className="login-title">eSafe Install Portal</h2>

        <div className="input-group">
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            placeholder="Enter your username"
            value={inputUsername}
            onChange={(e) => setUsername(e.target.value)}
            required
            aria-required="true"
            autoComplete="off"
          />
        </div>

        <div className="input-group">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            aria-required="true"
            autoComplete="off"
          />
        </div>

        {error && <p className="error" role="alert">{error}</p>}

        <p className="login-note">
          Do NOT save your login credentials if requested when logging in
        </p>

        {isLoading ? (
          <p className="loading">Loading...</p>
        ) : (
          <button type="submit" className="login-button">Log In</button>
        )}
      </form>
    </div>
  );
}

export default LoginPage;
